import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Search, Flame, Trophy, ClipboardCheck, GraduationCap, MessageCircle, BookOpen, FileText, Sparkles, Bot,
  AlertCircle, ArrowLeft, Clock, Percent,
} from "lucide-react";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import "./StudentActivity.css";

const FILTERS = [
  { key: "all", label: "Everything" },
  { key: "quiz", label: "Quizzes", types: ["quiz"] },
  { key: "course", label: "Course chapters", types: ["course_quiz"] },
  { key: "tutor", label: "AI tutor", types: ["tutor"] },
  { key: "other", label: "Other", types: ["summary", "explanation", "agent"] },
];

const TYPE_META = {
  quiz: { icon: ClipboardCheck, label: "Quiz" },
  course_quiz: { icon: GraduationCap, label: "Course quiz" },
  tutor: { icon: MessageCircle, label: "Asked the AI tutor" },
  summary: { icon: FileText, label: "Summary" },
  explanation: { icon: Sparkles, label: "Explanation" },
  agent: { icon: Bot, label: "AI agent" },
};

const when = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const StudentActivity = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(studentId || "");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [roster, setRoster] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/api/teacher/students").then((r) => setRoster(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(async (id, period) => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/api/teacher/student-activity/${encodeURIComponent(id.trim())}`, { params: { days: period } });
      setData(res.data);
    } catch (e) {
      setData(null);
      setError(e?.response?.data?.detail || "We couldn't load that student's activity. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (studentId) {
      setQuery(studentId);
      load(studentId, days);
    }
  }, [studentId, days, load]);

  const submit = (e) => {
    e.preventDefault();
    const id = query.trim();
    if (!id) return;
    if (id === studentId) load(id, days);
    else navigate(`/teacher/student-activity/${encodeURIComponent(id)}`);
  };

  const feed = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    return (data?.activity || []).filter((e) => !f?.types || f.types.includes(e.type));
  }, [data, filter]);

  const maxDay = Math.max(1, ...(data?.daily || []).map((d) => d.count));
  const s = data?.summary;
  const tiles = s && [
    { icon: Trophy, label: "Points", value: s.points },
    { icon: Flame, label: "Day streak", value: s.streak },
    { icon: ClipboardCheck, label: "Quizzes taken", value: s.quizzes_taken },
    { icon: Percent, label: "Average score", value: s.average_score == null ? "—" : `${s.average_score}%` },
    { icon: GraduationCap, label: "Chapters passed", value: s.chapters_passed },
    { icon: MessageCircle, label: "Tutor questions", value: s.tutor_questions },
  ];

  return (
    <Layout>
      <div className="sa-page">
        <button type="button" className="sa-back" onClick={() => navigate("/teacher-dashboard")}>
          <ArrowLeft size={16} /> Back to dashboard
        </button>
        <header className="sa-head">
          <h1>Student activity</h1>
          <p>Enter a student ID to see what that student has been doing: quizzes, course chapters, AI tutor questions and more. Students you look up are saved to your list.</p>
        </header>

        <form className="sa-search" onSubmit={submit} role="search">
          <Search size={18} aria-hidden="true" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Student ID, e.g. S-2026-AB12"
            aria-label="Student ID"
            list="sa-roster"
            autoComplete="off"
            spellCheck={false}
          />
          <datalist id="sa-roster">
            {roster.map((r) => <option key={r.id} value={r.student_id}>{r.name}</option>)}
          </datalist>
          <button type="submit" className="eg-btn eg-btn-accent" disabled={!query.trim() || loading}>
            {loading ? "Looking…" : "View activity"}
          </button>
        </form>

        {error && <div className="sa-alert" role="alert"><AlertCircle size={18} /> <span>{error}</span></div>}

        {!data && !error && !loading && (
          <div className="sa-empty">
            <Search size={28} />
            <p>Type a student ID above. Students can find theirs on their profile. You only see students whose ID you know.</p>
            {roster.length > 0 && (
              <div className="sa-roster" aria-label="Your students">
                {roster.slice(0, 8).map((r) => (
                  <button key={r.id} type="button" className="sa-chip" onClick={() => navigate(`/teacher/student-activity/${encodeURIComponent(r.student_id)}`)}>
                    {r.name} · {r.student_id}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {loading && !data && <div className="sa-card sa-skeleton" aria-busy="true">Loading activity…</div>}

        {data && (
          <>
            <section className="sa-card sa-profile">
              <div className="sa-avatar" aria-hidden="true">{data.student.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}</div>
              <div className="sa-profile-main">
                <h2>{data.student.name} {data.student.suspended && <span className="sa-tag warn">Suspended</span>}</h2>
                <p className="sa-meta">
                  <span>ID <strong>{data.student.student_id}</strong></span>
                  {data.student.class && <span>{data.student.class}</span>}
                  {data.student.school && <span>{data.student.school}</span>}
                  <span><Clock size={13} /> Last active {when(data.student.last_active)}</span>
                </p>
              </div>
              <label className="sa-period">
                Period
                <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={365}>Last year</option>
                </select>
              </label>
            </section>

            <section className="sa-tiles" aria-label="Summary">
              {tiles.map((t) => (
                <div key={t.label} className="sa-tile">
                  <t.icon size={18} aria-hidden="true" />
                  <strong>{t.value}</strong>
                  <span>{t.label}</span>
                </div>
              ))}
            </section>

            <div className="sa-grid">
              <section className="sa-card">
                <h3>Last 14 days</h3>
                <div className="sa-bars" role="img" aria-label="Activities per day for the last 14 days">
                  {data.daily.map((d) => (
                    <div key={d.date} className="sa-bar-col" title={`${d.date}: ${d.count} activities`}>
                      <div className="sa-bar" style={{ height: `${Math.max(4, (d.count / maxDay) * 100)}%` }} data-zero={d.count === 0} />
                      <span>{new Date(d.date + "T00:00:00").getDate()}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="sa-card">
                <h3>Subjects</h3>
                {data.subjects.length === 0 ? <p className="sa-muted">No quizzes yet.</p> : (
                  <ul className="sa-list">
                    {data.subjects.map((x) => (
                      <li key={x.subject}>
                        <span>{x.subject}</span>
                        <span className="sa-muted">{x.quizzes} quiz{x.quizzes === 1 ? "" : "zes"} · avg {x.average ?? "—"}%</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <section className="sa-card">
              <h3><BookOpen size={16} /> Course progress</h3>
              {data.courses.length === 0 ? <p className="sa-muted">Hasn't started a course yet.</p> : (
                <div className="sa-courses">
                  {data.courses.map((c) => (
                    <div key={`${c.subject}-${c.class}`} className="sa-course">
                      <div className="sa-course-top">
                        <strong>{c.subject}</strong>
                        <span className="sa-muted">{c.class} · {c.chapters_passed}{c.chapters_total ? ` of ${c.chapters_total}` : ""} chapters passed · last {when(c.last_activity)}</span>
                      </div>
                      <div className="sa-progress"><div style={{ width: `${c.percent ?? 0}%` }} /></div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="sa-card">
              <div className="sa-feed-head">
                <h3>Activity timeline</h3>
                <div className="sa-filters" role="tablist">
                  {FILTERS.map((f) => (
                    <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
                      className={`sa-chip ${filter === f.key ? "on" : ""}`} onClick={() => setFilter(f.key)}>{f.label}</button>
                  ))}
                </div>
              </div>
              {feed.length === 0 ? <p className="sa-muted">Nothing in this period.</p> : (
                <ol className="sa-feed">
                  {feed.map((e, i) => {
                    const meta = TYPE_META[e.type] || TYPE_META.agent;
                    const isQuiz = e.type === "quiz" || e.type === "course_quiz";
                    return (
                      <li key={`${e.at}-${i}`} className="sa-event">
                        <span className={`sa-ico t-${e.type}`}><meta.icon size={16} /></span>
                        <div className="sa-event-body">
                          <div className="sa-event-top">
                            <strong>{meta.label}{e.subject ? ` · ${e.subject}` : ""}</strong>
                            <time className="sa-muted" dateTime={e.at}>{when(e.at)}</time>
                          </div>
                          <p>{e.title}</p>
                          {isQuiz && e.total > 0 && (
                            <span className={`sa-score ${e.passed ? "pass" : "fail"}`}>{e.score}/{e.total} · {e.percentage}%</span>
                          )}
                          {e.detail && (
                            <>
                              <button type="button" className="sa-link" onClick={() => setOpen(open === i ? null : i)}>
                                {open === i ? "Hide answer" : "Show tutor's answer"}
                              </button>
                              {open === i && <p className="sa-answer">{e.detail}</p>}
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </>
        )}
      </div>
    </Layout>
  );
};

export default StudentActivity;
